import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wallet, TrendingUp, CalendarRange, Plus, Users2, Layers, Trash2, DollarSign, RotateCcw, AlertTriangle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend } from "recharts";

const CATEGORIES = [
  { v: "repair", l: "Repair" },
  { v: "inspection", l: "Inspection" },
  { v: "certificate", l: "Certificate" },
  { v: "other", l: "Other" },
];
const CATEGORY_COLORS = { repair: "#DC2626", inspection: "#01ADEF", certificate: "#059669", other: "#6B7280" };

export default function Billing() {
  const [summary, setSummary] = useState(null);
  const [charges, setCharges] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filter, setFilter] = useState({ customer_email: "all", category: "all", start: "", end: "" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_email: "", category: "inspection", amount: "", description: "", date: new Date().toISOString().slice(0, 10) });
  const [pendingDelete, setPendingDelete] = useState(null);
  const [voidForm, setVoidForm] = useState({ reason_category: "duplicate", reason_notes: "" });

  const loadSummary = async () => {
    try { const { data } = await api.get("/billing/admin/summary"); setSummary(data); }
    catch { toast.error("Failed to load summary"); }
  };
  const loadCharges = async () => {
    const params = {};
    if (filter.customer_email !== "all") params.customer_email = filter.customer_email;
    if (filter.category !== "all") params.category = filter.category;
    if (filter.start) params.start = filter.start;
    if (filter.end) params.end = filter.end;
    try { const { data } = await api.get("/charges", { params }); setCharges(data); }
    catch { toast.error("Failed to load charges"); }
  };
  const loadCustomers = async () => {
    try { const { data } = await api.get("/users"); setCustomers(data.filter(u => u.role === "customer")); }
    catch { /* ignore */ }
  };

  useEffect(() => { loadSummary(); loadCustomers(); }, []);
  useEffect(() => { loadCharges(); }, [filter]);

  const submit = async () => {
    if (!form.customer_email || !form.amount || Number(form.amount) <= 0) return toast.error("Customer & amount required");
    try {
      await api.post("/charges", { ...form, amount: Number(form.amount) });
      toast.success("Charge added");
      setOpen(false);
      setForm({ customer_email: "", category: "inspection", amount: "", description: "", date: new Date().toISOString().slice(0, 10) });
      loadSummary(); loadCharges();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    const payload = { ...voidForm };
    setPendingDelete(null);
    setVoidForm({ reason_category: "duplicate", reason_notes: "" });
    try {
      await api.delete(`/charges/${id}`, { params: payload });
      loadSummary(); loadCharges();
      toast("Charge voided", {
        description: `Reason: ${payload.reason_category}${payload.reason_notes ? " — " + payload.reason_notes : ""}`,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post(`/charges/${id}/restore`);
              toast.success("Charge restored");
              loadSummary(); loadCharges();
            } catch (e) { toast.error(e.response?.data?.detail || "Failed to restore"); }
          },
        },
        duration: 8000,
      });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!summary) return <div className="p-8 text-gray-500">Loading billing…</div>;
  const k = summary.kpis;
  const monthly = summary.last_12_months.map(m => ({ ...m, month: m.month.slice(2) }));

  return (
    <div className="p-6 md:p-8" data-testid="billing-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Finance</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Billing &amp; Revenue</h1>
          <p className="text-sm text-gray-500 mt-1">Total spend by month, year, and to-date — plus per-customer breakdown across repairs, inspections, and certificates.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-charge-btn" className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]"><Plus className="w-4 h-4 mr-2" />Add Charge</Button></DialogTrigger>
          <DialogContent className="rounded-md max-w-md">
            <DialogHeader><DialogTitle className="font-display">Add Charge</DialogTitle><DialogDescription className="text-xs text-gray-500">Manually record a billable line for a customer (inspection fee, certificate, or other).</DialogDescription></DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">Customer *</Label>
                <Select value={form.customer_email} onValueChange={v => setForm({ ...form, customer_email: v })}><SelectTrigger className="rounded-md mt-1" data-testid="charge-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger><SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.email}>{c.name} ({c.email})</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">Category</Label>
                  <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}><SelectTrigger className="rounded-md mt-1"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map(c => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">Date</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-md mt-1" /></div>
              </div>
              <div><Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">Amount (USD) *</Label><Input data-testid="charge-amount-input" type="number" min={0} step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className="rounded-md mt-1 font-mono" /></div>
              <div><Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-md mt-1" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="save-charge-btn" onClick={submit} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi label="This Month" value={k.total_this_month} icon={CalendarRange} accent="bg-[#01ADEF]" testid="billing-kpi-month" money />
        <Kpi label="Year to Date" value={k.total_ytd} icon={TrendingUp} accent="bg-[#059669]" testid="billing-kpi-ytd" money />
        <Kpi label="All-time" value={k.total_all_time} icon={Wallet} accent="bg-[#D97706]" testid="billing-kpi-total" money />
        <Kpi label="Customers Billed" value={k.num_customers} icon={Users2} accent="bg-[#6B7280]" testid="billing-kpi-customers" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="lg:col-span-2 bg-white border border-gray-200 p-5">
          <div className="mb-3">
            <h2 className="font-display text-lg font-bold tracking-tight">Monthly Revenue — Last 12 months</h2>
            <p className="text-xs text-gray-500 mt-0.5">Total charges per month across all customers</p>
          </div>
          {monthly.every(m => m.amount === 0) ? (
            <div className="text-center py-16 text-sm text-gray-500">No spend recorded yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="month" tick={{ fill: "#6B7280", fontSize: 12 }} />
                <YAxis tick={{ fill: "#6B7280", fontSize: 12 }} />
                <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #D1D5DB" }} formatter={(v) => `$${v}`} />
                <Line type="monotone" dataKey="amount" stroke="#01ADEF" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-white border border-gray-200 p-5">
          <div className="mb-3">
            <h2 className="font-display text-lg font-bold tracking-tight">By Category</h2>
            <p className="text-xs text-gray-500 mt-0.5">All-time distribution</p>
          </div>
          {summary.by_category.length === 0 ? (
            <div className="text-center py-16 text-sm text-gray-500">No categories yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={summary.by_category} dataKey="amount" nameKey="category" innerRadius={45} outerRadius={80}>
                  {summary.by_category.map((c) => <Cell key={`bcat-${c.category}`} fill={CATEGORY_COLORS[c.category] || "#6B7280"} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #D1D5DB" }} formatter={(v) => `$${v}`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className="bg-white border border-gray-200">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-2"><Users2 className="w-4 h-4 text-[#01ADEF]" /><h2 className="font-display text-lg font-bold tracking-tight">Top Customers by Spend</h2></div>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-200 bg-gray-50"><Th>Customer</Th><Th className="text-right">Total Spend</Th></tr></thead>
            <tbody data-testid="top-customers-table">
              {summary.by_customer.length === 0 ? <tr><td colSpan={2} className="text-center py-10 text-gray-500">No customer spend recorded.</td></tr> :
                summary.by_customer.slice(0, 10).map((c, idx) => (
                  <tr key={c.customer_email} className="border-b border-gray-100" data-testid={`top-customer-${idx}`}>
                    <Td><div className="font-semibold">#{idx + 1} {c.customer_email}</div></Td>
                    <Td className="font-mono text-right">${c.amount.toFixed(2)}</Td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="bg-white border border-gray-200">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-2"><Layers className="w-4 h-4 text-[#01ADEF]" /><h2 className="font-display text-lg font-bold tracking-tight">Revenue by Year</h2></div>
          {summary.by_year.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">No yearly data yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={summary.by_year} margin={{ top: 20, right: 20, bottom: 10, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="year" tick={{ fill: "#6B7280", fontSize: 12 }} />
                <YAxis tick={{ fill: "#6B7280", fontSize: 12 }} />
                <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #D1D5DB" }} formatter={(v) => `$${v}`} />
                <Bar dataKey="amount" fill="#01ADEF" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Charges list with filters */}
      <div className="bg-white border border-gray-200">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-display text-lg font-bold tracking-tight flex items-center gap-2"><DollarSign className="w-4 h-4 text-[#01ADEF]" />Charges Ledger</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={filter.customer_email} onValueChange={v => setFilter({ ...filter, customer_email: v })}>
              <SelectTrigger className="rounded-md w-52 h-9" data-testid="filter-customer"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All customers</SelectItem>{customers.map(c => <SelectItem key={c.id} value={c.email}>{c.email}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filter.category} onValueChange={v => setFilter({ ...filter, category: v })}>
              <SelectTrigger className="rounded-md w-36 h-9" data-testid="filter-category"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All categories</SelectItem>{CATEGORIES.map(c => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="date" value={filter.start} onChange={e => setFilter({ ...filter, start: e.target.value })} className="rounded-md w-36 h-9" data-testid="filter-start" />
            <Input type="date" value={filter.end} onChange={e => setFilter({ ...filter, end: e.target.value })} className="rounded-md w-36 h-9" data-testid="filter-end" />
          </div>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-gray-200 bg-gray-50"><Th>Date</Th><Th>Customer</Th><Th>Category</Th><Th>Description</Th><Th>Source</Th><Th className="text-right">Amount</Th><Th></Th></tr></thead>
          <tbody data-testid="charges-table">
            {charges.length === 0 ? <tr><td colSpan={7} className="text-center py-10 text-gray-500">No charges match the current filters.</td></tr> :
              charges.map(c => (
                <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`charge-row-${c.id}`}>
                  <Td className="font-mono">{c.date}</Td>
                  <Td>{c.customer_email}</Td>
                  <Td><span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border" style={{ background: `${CATEGORY_COLORS[c.category] || "#6B7280"}20`, color: CATEGORY_COLORS[c.category] || "#6B7280", borderColor: CATEGORY_COLORS[c.category] || "#6B7280" }}>{c.category}</span></Td>
                  <Td className="text-gray-600">{c.description || "—"}</Td>
                  <Td className="text-xs uppercase tracking-wider text-gray-500">{c.source || "manual"}</Td>
                  <Td className="font-mono text-right">${Number(c.amount || 0).toFixed(2)}</Td>
                  <Td><button onClick={() => setPendingDelete(c)} data-testid={`delete-charge-${c.id}`} className="text-gray-400 hover:text-[#DC2626]"><Trash2 className="w-4 h-4" /></button></Td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Confirm delete dialog */}
      <Dialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <DialogContent className="rounded-md max-w-md" data-testid="confirm-delete-charge">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-[#DC2626]" />Delete this charge?</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">Soft-delete removes the charge from all totals but preserves it for audit. You can undo within 8 seconds.</DialogDescription>
          </DialogHeader>
          <div className="py-3">
            {pendingDelete && (
              <div className="border border-gray-200 bg-gray-50 p-4 text-sm space-y-1">
                <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wider font-semibold text-gray-500">Date</span><span className="font-mono">{pendingDelete.date}</span></div>
                <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wider font-semibold text-gray-500">Customer</span><span>{pendingDelete.customer_email}</span></div>
                <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wider font-semibold text-gray-500">Category</span><span className="capitalize">{pendingDelete.category}</span></div>
                <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wider font-semibold text-gray-500">Amount</span><span className="font-mono font-bold text-lg">${Number(pendingDelete.amount || 0).toFixed(2)}</span></div>
                {pendingDelete.description && <div className="pt-2 text-xs text-gray-600 border-t border-gray-200 italic">&ldquo;{pendingDelete.description}&rdquo;</div>}
              </div>
            )}
            <p className="text-xs text-gray-600 mt-3 flex items-start gap-2 border-l-2 border-[#01ADEF] pl-3">
              <RotateCcw className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[#01ADEF]" />
              <span>This is a <b>soft delete</b>. The charge is hidden from totals but preserved for the audit trail. You&apos;ll have 8 seconds to undo, and admins can always restore it via the API.</span>
            </p>

            {/* Void Reason */}
            <div className="mt-4 space-y-2 border-t border-gray-200 pt-3">
              <Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">Void Reason *</Label>
              <Select value={voidForm.reason_category} onValueChange={v => setVoidForm({ ...voidForm, reason_category: v })}>
                <SelectTrigger className="rounded-md" data-testid="void-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="duplicate">Duplicate charge</SelectItem>
                  <SelectItem value="billing_error">Billing error</SelectItem>
                  <SelectItem value="customer_credit">Customer credit / goodwill</SelectItem>
                  <SelectItem value="adjustment">Adjustment / correction</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              <Textarea data-testid="void-reason-notes" value={voidForm.reason_notes} onChange={e => setVoidForm({ ...voidForm, reason_notes: e.target.value })} placeholder="Optional notes for the audit log…" className="rounded-md text-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setPendingDelete(null)} data-testid="cancel-delete-charge">Cancel</Button>
            <Button onClick={() => remove(pendingDelete.id)} data-testid="confirm-delete-charge-btn" className="rounded-md bg-[#DC2626] hover:bg-[#B91C1C]">Delete Charge</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Th = ({ children, className = "" }) => <th className={`text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold text-gray-600 ${className}`}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
const Kpi = ({ label, value, icon: Icon, accent, testid, money }) => (
  <div className="bg-white border border-gray-200 p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-3"><span className="text-xs uppercase tracking-wider font-semibold text-gray-500">{label}</span><div className={`w-8 h-8 ${accent} flex items-center justify-center`}><Icon className="w-4 h-4 text-white" strokeWidth={2.5} /></div></div>
    <div className="font-display text-3xl md:text-4xl font-bold tracking-tight">{money ? `$${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : value}</div>
  </div>
);
